import React from 'react';
import Modal from './Modal';

interface TermsOfServiceProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
}

const TermsOfService: React.FC<TermsOfServiceProps> = ({ isOpen, onClose, onAccept }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onAccept={onAccept}
      title="Terms of Service"
      acceptText="I Accept"
      closeText="Close"
      maxWidth="1000px"
    >
      <div className="terms-content">
        <p className="last-updated mb-4">Last Updated: January 15, 2025</p>

        {/* Introduction */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">1. Introduction</h3>
          <p>
            Welcome to <strong>Swipey.ai</strong> (the "Service"). By accessing or using our Service, you agree to comply
            with and be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the
            Service.
          </p>
          <p className="mt-2">
            The Service is provided by <strong>InvAI Tech</strong> ("we," "us," or "our"). These Terms govern your access
            to and use of the Service, including any content, functionality, and services offered on or through{' '}
            <a href="https://swipey.ai/" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
              swipey.ai
            </a>
            .
          </p>
        </div>

        {/* Age Restriction */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">2. Age Restriction</h3>
          <p>
            You must be at least <strong>18 years old</strong> to use the Service. By using the Service, you represent and
            warrant that you are at least 18 years of age. We do not knowingly collect or solicit information from anyone
            under the age of 18. If we learn that we have collected personal information from a person under 18, we will
            delete that information as quickly as possible.
          </p>
        </div>

        {/* Account Registration */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">3. Account Registration</h3>
          <p>
            To access certain features of the Service, you may be required to create an account. You agree to provide
            accurate, current, and complete information during the registration process and to update such information to
            keep it accurate, current, and complete.
          </p>
          <p className="mt-2">
            You are responsible for maintaining the confidentiality of your account credentials and for all activities that
            occur under your account. You agree to notify us immediately of any unauthorized use of your account.
          </p>
        </div>

        {/* User Conduct */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">4. User Conduct</h3>
          <p>You agree not to use the Service to:</p>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li>Violate any applicable laws or regulations.</li>
            <li>Infringe upon the rights of others, including intellectual property rights.</li>
            <li>Transmit any harmful, threatening, abusive, or otherwise objectionable content.</li>
            <li>Engage in any activity that could damage, disable, or impair the Service.</li>
            <li>Attempt to gain unauthorized access to any part of the Service or related systems.</li>
          </ul>
        </div>

        {/* Content Ownership */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">5. Content Ownership and Licensing</h3>
          <div className="terms-subsection pl-4 mt-4">
            <h4 className="font-medium mb-1">5.1 Our Content</h4>
            <p>
              All content, features, and functionality available through the Service, including but not limited to text,
              graphics, logos, images, and software, are the property of <strong>InvAI Tech</strong> or its licensors and
              are protected by copyright, trademark, and other intellectual property laws.
            </p>
          </div>
          <div className="terms-subsection pl-4 mt-4">
            <h4 className="font-medium mb-1">5.2 User Content</h4>
            <p>
              You retain ownership of any content you submit, post, or display on or through the Service ("User Content").
              By submitting User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce,
              modify, and distribute such content in connection with the operation of the Service.
            </p>
          </div>
        </div>

        {/* AI-Generated Content */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">6. AI-Generated Content</h3>
          <p>
            The Service utilizes artificial intelligence to generate content, including but not limited to text, images,
            and interactive features. <strong>AI-generated content is provided "as is" without any warranties.</strong> We
            do not guarantee the accuracy, completeness, or appropriateness of AI-generated content. You acknowledge that
            AI-generated content may contain errors or inaccuracies and should not be relied upon without independent
            verification.
          </p>
        </div>

        {/* Prohibited Uses */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">7. Prohibited Uses</h3>
          <p>You may not use the Service for any illegal or unauthorized purpose. Prohibited uses include, but are not limited to:</p>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li>Harassment, abuse, or harm of another person.</li>
            <li>Impersonation of any person or entity.</li>
            <li>Distribution of spam or unsolicited communications.</li>
            <li>Interference with or disruption of the Service or servers connected to the Service.</li>
            <li>Use of automated systems (bots, scrapers, etc.) to access the Service without our prior written consent.</li>
          </ul>
        </div>

        {/* Payment and Subscriptions */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">8. Payment and Subscriptions</h3>
          <p>
            Certain features of the Service may require payment. By subscribing to a paid plan, you agree to pay all
            applicable fees as described on the Service. All fees are non-refundable unless otherwise stated.
          </p>
          <p className="mt-2">
            Subscriptions will automatically renew unless you cancel your subscription before the renewal date. You can
            manage your subscription settings through your account.
          </p>
        </div>

        {/* Termination */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">9. Termination</h3>
          <p>
            We reserve the right to suspend or terminate your access to the Service at any time, with or without cause,
            and with or without notice. Upon termination, your right to use the Service will immediately cease.
          </p>
          <p className="mt-2">
            You may terminate your account at any time by contacting us at{' '}
            <a href="mailto:admin@invai.tech" className="text-blue-500 underline">
              admin@invai.tech
            </a>
            .
          </p>
        </div>

        {/* Disclaimer of Warranties */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">10. Disclaimer of Warranties</h3>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
            INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
            NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
          </p>
        </div>

        {/* Limitation of Liability */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">11. Limitation of Liability</h3>
          <p>
            TO THE FULLEST EXTENT PERMITTED BY LAW, <strong>InvAI Tech</strong> SHALL NOT BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED
            DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
          </p>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li>Your use or inability to use the Service.</li>
            <li>Any unauthorized access to or use of our servers and/or any personal information stored therein.</li>
            <li>Any bugs, viruses, or other harmful code that may be transmitted to or through the Service.</li>
            <li>Any errors or omissions in any content or for any loss or damage incurred as a result of the use of any content posted, emailed, transmitted, or otherwise made available through the Service.</li>
          </ul>
        </div>

        {/* Indemnification */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">12. Indemnification</h3>
          <p>
            You agree to indemnify, defend, and hold harmless <strong>InvAI Tech</strong>, its officers, directors,
            employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including
            reasonable attorneys' fees, arising out of or in any way connected with your access to or use of the Service,
            your violation of these Terms, or your infringement of any intellectual property or other rights of any third
            party.
          </p>
        </div>

        {/* Governing Law */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">13. Governing Law</h3>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which{' '}
            <strong>InvAI Tech</strong> operates, without regard to its conflict of law provisions.
          </p>
        </div>

        {/* Changes to Terms */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">14. Changes to These Terms</h3>
          <p>
            We reserve the right to modify or replace these Terms at any time. If we make material changes, we will notify
            you by updating the "Last Updated" date at the top of these Terms. Your continued use of the Service after any
            such changes constitutes your acceptance of the new Terms.
          </p>
        </div>

        {/* Contact Information */}
        <div className="terms-section mb-6">
          <h3 className="font-semibold text-lg mb-2">15. Contact Us</h3>
          <p>
            If you have any questions about these Terms, please contact us at:{' '}
            <a href="mailto:admin@invai.tech" className="text-blue-500 underline">
              admin@invai.tech
            </a>
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default TermsOfService;
